import { Controller } from '@hotwired/stimulus'
import { emiteEventoDelEmbudo } from 'lib/analitica_del_embudo'

// Botón «Add us on Google» de Google (Preferred Sources).
//
// La carga vive aquí, en el contenedor, y no en el <head>: al navegar con un clic la app solo
// reemplaza turbo-frame#content y el servidor responde con un layout sin <head>, así que lo que
// hubiera allí no llega. Y publisher.js solo pinta los contenedores que existen cuando carga: a
// los que aparecen después hay que pedírselo con PREFERRED_SOURCE.api.init(). Google documenta
// init() como configuración, sobre el objeto que entrega la cola PREFERRED_SOURCE; no documenta
// la propiedad api, llamarlo más de una vez ni que pinte contenedores nuevos: eso se vio
// inspeccionando el objeto global en producción y probándolo allí.
export const URL_PUBLISHER = 'https://news.google.com/swg/js/v1/publisher.js'

// Placeholder tomado de cookieWallHandler, no una confirmación: el propósito que de verdad
// habilita a Google hay que confirmarlo en el panel de Didomi.
const PROPOSITO = 'create_ads_profile'

const SELECTOR_CONTENEDOR = '[data-controller~="fuente-preferida"]'

// PDT-RNK-5611. Se mide el clic, no el alta como fuente preferida: Google no avisa de lo segundo.
export const EVENTO_CLIC = 'preferred_source_button_click'

export default class extends Controller {
  static values = { sinGate: Boolean }

  connect () {
    // El botón de Google vive en un shadow root abierto; el clic es composed y llega aquí
    // retargeteado al contenedor.
    this.element.addEventListener('click', this.registraClic)

    if (this.sinGateValue) {
      cargarOPintar()
    } else {
      esperarConsentimiento()
    }
  }

  disconnect () {
    this.element.removeEventListener('click', this.registraClic)
  }

  // Ni cancela ni detiene el clic: el flujo de Google tiene que abrirse igual, mida o no mida.
  registraClic = (evento) => {
    try {
      if (!nacioEnElBotonDeGoogle(evento, this.element)) return
      emiteEventoDelEmbudo(EVENTO_CLIC, paramsDelClic())
    } catch {
      // La analítica no puede romper el botón.
    }
  }
}

// El contenedor ocupa todo el ancho y reserva su hueco aunque Google no pinte nada (sin
// consentimiento en es/us): un clic en ese espacio no es un clic en el botón. Solo cuenta el
// que nace dentro del shadow root que rellena publisher.js; composedPath() lo expone porque
// es abierto.
//
// El `instanceof Node` no es defensivo: la ruta de un clic que no nace en el botón se recorre
// entera y termina en Window, que no es un Node y hace lanzar a contains(). Sin el filtro, ese
// TypeError lo absorbería el catch de registraClic en cada clic del hueco, y entonces un fallo
// real de la analítica sería indistinguible del caso normal.
function nacioEnElBotonDeGoogle (evento, contenedor) {
  const raiz = contenedor.querySelector('[google-add-preferred-source-btn]')?.shadowRoot
  if (!raiz) return false

  return evento.composedPath().some(nodo => nodo instanceof Node && nodo !== raiz && raiz.contains(nodo))
}

// `site` y `url` van explícitos porque las propiedades comunes del embudo no los incluyen
// (`locale` no es el dominio). La url va sin query ni hash: identifica el artículo, y la query
// puede traer tokens de enlaces de newsletter; los utm_* ya viajan en las propiedades comunes.
// Se lee al pulsar: tras navegar por turbo-frame (data-turbo-action="advance") la location ya es
// la del artículo actual.
function paramsDelClic () {
  return {
    site: window.location.hostname,
    url: window.location.origin + window.location.pathname
  }
}

function cargarOPintar () {
  const api = window.PREFERRED_SOURCE?.api
  if (api) {
    api.init()
    return
  }
  // Ya se está descargando: al cargar pinta todos los contenedores que haya en ese momento.
  if (document.querySelector(`script[src="${URL_PUBLISHER}"]`)) return

  const script = document.createElement('script')
  script.async = true
  script.src = URL_PUBLISHER
  document.head.appendChild(script)
}

// `logged_in` y no `token_autorizacion`, que es HttpOnly (auditoría H19).
const tieneSesion = () => document.cookie.split(';').some(c => c.trim().startsWith('logged_in='))

const hayConsentimiento = () => window.Didomi?.getUserStatus?.()?.purposes?.consent?.enabled?.includes(PROPOSITO)

function cargarSiHayConsentimiento () {
  // El aviso de consentimiento puede llegar estando el lector en una página sin botón.
  if (!document.querySelector(SELECTOR_CONTENEDOR)) return
  if (hayConsentimiento() || tieneSesion()) cargarOPintar()
}

function esperarConsentimiento () {
  const didomiCargado = typeof window.Didomi?.getUserStatus === 'function'

  if (didomiCargado) {
    cargarSiHayConsentimiento()
  } else {
    window.didomiOnReady = window.didomiOnReady || []
    window.didomiOnReady.push(cargarSiHayConsentimiento)
  }

  // Un único oyente por página, aunque el lector abra varios artículos con clics.
  if (window.fuentePreferidaEscuchaConsentimiento) return
  window.fuentePreferidaEscuchaConsentimiento = true
  if (didomiCargado) {
    window.Didomi.on('consent.changed', cargarSiHayConsentimiento)
  } else {
    window.didomiEventListeners = window.didomiEventListeners || []
    window.didomiEventListeners.push({ event: 'consent.changed', listener: cargarSiHayConsentimiento })
  }
}
